import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { LegalLayout, H2, P, List, Note } from "./LegalLayout";
import { FOUND_CONFIG, GREEN, contactTarget } from "./foundConfig";

export default function DeleteAccount() {
  const c = FOUND_CONFIG;
  const t = contactTarget();

  return (
    <LegalLayout
      title="Delete Your FOUND Account"
      description="How to request deletion of your FOUND account and associated data, and what may be retained."
    >
      <P>
        You can ask us to delete your FOUND account at any time. This page explains how to request
        deletion and what happens to your data. It is intended to be linked from the Apple App Store
        and Google Play.
      </P>

      <H2>How to request deletion</H2>
      <P>Until in-app deletion is available on every platform, you can request account deletion here:</P>
      <div className="my-6">
        <Link
          to="/found/support"
          data-testid="request-deletion-btn"
          className="inline-flex items-center gap-3 font-manrope font-semibold text-sm tracking-wider px-7 py-4 text-black"
          style={{ backgroundColor: GREEN }}
        >
          REQUEST ACCOUNT DELETION
          <ArrowUpRight size={16} />
        </Link>
      </div>
      <P>
        On the Support page, select <strong>Account Deletion</strong> and include the email or
        username associated with your FOUND account so we can verify the request.
        {t.isEmail && <> You can also email <a href={t.href} className="text-white underline">{t.href.replace("mailto:", "")}</a>.</>}
      </P>
      <Note>
        This page provides an honest deletion-request process rather than a button that does not
        work. It is structured so an automated in-app and web deletion flow can be connected here
        once the FOUND backend deletion API is live. The FOUND mobile app will also include an
        in-app account deletion option to meet Apple and Google requirements.
      </Note>

      <H2>What deletion removes</H2>
      <P>When your request is processed, we permanently remove or anonymise applicable data, which may include:</P>
      <List items={[
        "Profile information",
        "Artist or Scout account information",
        "Uploads (music, video and artwork)",
        "Comments and Scout Posts",
        "Likes and saves",
        "Follows",
        "Scout history, where legally and technically appropriate",
        "Other associated personal data",
      ]} />

      <H2>What may be retained</H2>
      <P>Some information may need to be kept for a limited period where required for:</P>
      <List items={[
        "Legal obligations",
        "Fraud prevention",
        "Security",
        "Disputes and enforcement",
        "Financial records, if paid services have been used",
      ]} />
      <P>
        Retained data is limited to what is necessary and is deleted or anonymised once no longer
        required. For more detail, see the{" "}
        <Link to="/found/privacy" className="text-white underline">FOUND Privacy Policy</Link>.
      </P>
    </LegalLayout>
  );
}
